import { faCaretDown, faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICorporationResponse } from '@kcftech/prometheus-client';
import { Checkbox } from 'components/forms/Checkbox';
import { memo, useState } from 'react';
import { buildHighlighter } from 'shared/fuzzy_search/render_search';
import { sortBy } from 'utils/array';
import { ProgressiveList } from '../components/ProgressiveList';
import { CorpExpandButton, CorpRow, LocationRow } from './MissionControlEntityNav.styled';
import { MissionControlOrgs } from './mission_control_entity_store';
import {
    useCorpSearchFilterActive,
    useCorpSearchInfo,
    useLocationHighlight,
} from './search_state';

type CorpTreeItemProps = Readonly<{
    corporation: ICorporationResponse;
}>;

const renderHighlight = buildHighlighter((item) => <mark>{item}</mark>);

// Despite having react compiler enabled, this memo yielded perf improvements
// (dev mode?)
export const CorpTreeItem = memo(function CorpTreeItem({
    corporation,
}: CorpTreeItemProps) {
    const filterActive = useCorpSearchFilterActive();
    const {
        visible,
        highlight: corpHighlight,
        matchingLocationIds,
    } = useCorpSearchInfo(corporation.Id);

    const isCorpSelected = MissionControlOrgs.useIsCorpSelected(corporation.Id);
    const toggleCorporation = MissionControlOrgs.useToggleCorporation();
    const getLocSelection = MissionControlOrgs.useLocSelection();
    const locSelection = getLocSelection(corporation.Id);

    // Auto-expand on mount when locations are selected, but don't
    // auto-collapse when the user unchecks the last location.
    const [expanded, setExpanded] = useState(() => locSelection !== 'none');
    const isExpanded = expanded || filterActive;

    const sortedLocations = sortBy(corporation.Locations, (l) =>
        l.LocationName.toLowerCase(),
    );

    const filteredLocations = matchingLocationIds
        ? sortedLocations.filter((l) => matchingLocationIds.has(l.LocationId))
        : sortedLocations;

    if (!visible) {
        return null;
    }

    const corpChecked: boolean | 'indeterminate' = isCorpSelected
        ? true
        : locSelection !== 'none'
          ? 'indeterminate'
          : false;

    return (
        <div>
            <CorpRow>
                <CorpExpandButton
                    type="button"
                    onClick={() => setExpanded(!isExpanded)}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                    <FontAwesomeIcon icon={isExpanded ? faCaretDown : faCaretRight} />
                </CorpExpandButton>
                <Checkbox
                    checked={corpChecked}
                    onChange={() => toggleCorporation(corporation.Id)}
                    label={renderHighlight(corporation.Name, corpHighlight)}
                />
            </CorpRow>
            {isExpanded && (
                <div>
                    <ProgressiveList
                        items={filteredLocations.map((location) => [
                            location.LocationId,
                            <LocationCheckbox
                                key={location.LocationId}
                                locationId={location.LocationId}
                                locationName={location.LocationName}
                            />,
                        ])}
                    />
                </div>
            )}
        </div>
    );
});

// ---

type LocationCheckboxProps = Readonly<{
    locationId: string;
    locationName: string;
}>;

function LocationCheckbox({ locationId, locationName }: LocationCheckboxProps) {
    const checked = MissionControlOrgs.useIsLocationSelected(locationId);
    const toggleLocation = MissionControlOrgs.useToggleLocation();
    const highlightRanges = useLocationHighlight(locationId);
    return (
        <LocationRow>
            <Checkbox
                checked={checked}
                onChange={() => toggleLocation(locationId)}
                label={renderHighlight(locationName, highlightRanges)}
            />
        </LocationRow>
    );
}
